import React, { useContext, useEffect, useMemo, useState } from 'react'

import { COMPONENTS_CODE } from '../../assets/ts/data/common-metadata'
import {
  COLUMN_NAMES as DUEOUT_COLUMN_NAMES,
  DueOutFetchArgs,
} from '../../assets/ts/data/dueout/dueout'
import { STATUS_CODE as DUEOUT_STATUS_CODE } from '../../assets/ts/data/dueout/dueout-status-metadata'
import {
  COLUMN_NAMES as INQUIRY_COLUMN_NAMES,
  InquiryFetchArgs,
} from '../../assets/ts/data/inquiry/inquiry'
import { PascodeMetadata } from '../../assets/ts/data/pascode-metadata'
import { twoDigits } from '../../assets/ts/formatting/formatting'
import {
  generateCodeOptionOnLength,
  getFilteredItems,
  getMAJCOMCode,
  getUniqueCodes,
} from '../../assets/ts/general/general'
import Announcements from '../../components/announcements/announcement'
import Buttons from '../../components/buttons/buttons'
import HomeCalendar from '../../components/calendar/calendar-items'
import KnowledgeBase from '../../components/knowledge-base/knowledge-base'
import PageOverlayBusy from '../../components/page-overlay/page-overlay-busy'
import Tabs from '../../components/tabs/tabs'
import useDueoutorderByCounts from '../../hooks/dueout/use-dueout-charts-count'
import useDueOutCount from '../../hooks/dueout/use-dueout-count'
import useDueOutDistinctValues from '../../hooks/dueout/use-dueout-distinct-values'
import useInquiryCategoryCounts from '../../hooks/inquiry/use-inquiry-category-counts'
import useInquiryCount from '../../hooks/inquiry/use-inquiry-count'
import useInquiryDistinctValues from '../../hooks/inquiry/use-inquiry-distinct-values'
import useTotalByMonthCounts from '../../hooks/inquiry/use-total-by-months-count'
import usePascodeMetadata from '../../hooks/use-pascode-metadata'
import { MetadataContext } from '../../store/metadata-context-provider'
import { PreviousPageContext } from '../../store/previous-page-provider'
import { RoleContext } from '../../store/role-context-provider'
import { UserContext } from '../../store/user-context-provider'
import {
  ALL_SELECT_OPTION,
  ALL_SELECT_VALUE,
  ALL_SELECT_VALUE_STRING,
  INQUIRY_STATUS_IDS,
} from '../../utils/constants/global-constants'
import CustomSelect from '../../utils/controls/custom-select'
import { optionsTypes } from '../../utils/controls/select-options-data'
import AdminDashboardFilters from './admin-dashboard-filters'
import AdminDashBoardTabContent from './admin-dashboard-tabcontent'
import './home.css'

interface Props {
  CategoryOptions: optionsTypes[]
  ComponentOptions: optionsTypes[]
  MajcomOptions: optionsTypes[]
  SectionOptions: optionsTypes[]
}
interface InitialContentDropDownValues {
  categoryValue: optionsTypes
  componentValue: optionsTypes
  majcomValue: optionsTypes
  sectionValue: optionsTypes
}

type ModeType = 'dueout' | 'inquiry'

type TabVisibility = {
  afrc: boolean
  ang: boolean
  regAF: boolean
}

// to-do:
// Servicing organization is currently just stubbed out and defaulted to
// 96 MPF.  This will need to be implemented.
// In the future, the avaialable servicing organization values will come from
// the metadata context, filtered by the current user's roles.

const initialInquiryFilter: any = {
  CSSPASCODEFilter: -1,
  assignedComponentCodeFilter: ALL_SELECT_VALUE,
  assignedToLocationFilter: '',
  categoryCodeFilter: ALL_SELECT_VALUE,
  currentlyServicingByFilter: '',
  isArchivedFilter: false,
  isAscending: false,
  isViewApplied: true,
  orderBy: INQUIRY_COLUMN_NAMES.ItemModified,
  organizationFilter: '',
  otherFilter: null,
  savedFilter: false,
  searchFilter: '',
  sectionCodeFilter: ALL_SELECT_VALUE,
  servicingByFilter: ALL_SELECT_VALUE_STRING,
  servicingMAJCOMCodeFilter: ALL_SELECT_VALUE,
  servicingOrganizationFilter: ALL_SELECT_VALUE_STRING,
  statusCodesOmittedFilter: [
    INQUIRY_STATUS_IDS.Saved,
    INQUIRY_STATUS_IDS.Resolved,
    INQUIRY_STATUS_IDS.Closed,
    INQUIRY_STATUS_IDS.Canceled,
  ],
  submittedFilter: '',
  yearFilter: ALL_SELECT_VALUE,
}

const initialDueOutFilter: DueOutFetchArgs = {
  CSSPASCODEFilter: -1,
  assignedComponentCodeFilter: ALL_SELECT_VALUE,
  assignedToLocationFilter: '',
  categoryCodeFilter: ALL_SELECT_VALUE,
  hasClosedAndCanceled: false,
  isArchivedFilter: false,
  isAscending: false,
  isViewApplied: true,
  orderBy: DUEOUT_COLUMN_NAMES.ItemModified,
  sectionCodeFilter: ALL_SELECT_VALUE,
  servicingByFilter: ALL_SELECT_VALUE_STRING,
  servicingMAJCOMCodeFilter: ALL_SELECT_VALUE,
  servicingOrganizationFilter: ALL_SELECT_VALUE_STRING,
  statusCodesOmittedFilter: [DUEOUT_STATUS_CODE.SAVED],
  yearFilter: ALL_SELECT_VALUE,
}
const initialTabVisibility: TabVisibility = {
  afrc: false,
  ang: false,
  regAF: false,
}

const AdminView = ({ CategoryOptions, ComponentOptions, MajcomOptions, SectionOptions }: Props) => {
  const determineMode = (previousPage: ModeType, kpiClicked: boolean) => {
    let mode: ModeType = 'inquiry'
    if (`${previousPage}` === '/dueouts' && kpiClicked) {
      mode = 'dueout'
      setKPIClicked(false)
    }
    if (`${previousPage}` === '/inquiries') {
      mode = 'inquiry'
      setKPIClicked(false)
    }
    return mode
  }
  const { 
    categories, 
    categoryValue, 
    componentValue, 
    majcomValue, 
    majcoms, 
    sectionValue, 
    setCategoryValue, 
    setComponentValue, 
    setMajcomValue, 
    setSectionValue 
  } = useContext(MetadataContext)
  
  const {
    initialInquiryAndDueoutFilter,
    isAFRCOwner,
    isANGOwner,
    isAssignedComponentOwner,
    isCPOwner,
    isCSS,
    isLeadership,
    isMAJCOMOwner,
    isMPFOwner,
    isRegAFOwner,
    isSiteAdmin,
    servincingComponent,
  } = useContext(RoleContext)
  const { kpiClicked, previousPage, setKPIClicked } = useContext(PreviousPageContext)
  const { userProfile } = useContext(UserContext)
  const { me } = useContext(UserContext)

  const UserRoleCodes: Partial<DueOutFetchArgs | InquiryFetchArgs> = {
    userLoginRole: {
      CSSPASCODEFilter: initialInquiryAndDueoutFilter?.cssPASCODES,
      usersAssignedComponentCode: initialInquiryAndDueoutFilter?.assignedComponentCodeFilter,
      usersMajcomCodeFilter: initialInquiryAndDueoutFilter?.majcomCodeFilter,
      usersSectionCodeFilter: initialInquiryAndDueoutFilter?.sectionCodeFilter,
      usersServicingOrganizationFilter: initialInquiryAndDueoutFilter?.servicingOrganizationFilter,
      usersServicingOrganizationWithoutSectionFilter:
        initialInquiryAndDueoutFilter?.servicingOrganizationWithoutSectionFilter,
    },
  }
  const userComponentCode = userProfile[0].AssignedComponentCode
  const [selectedTab, setSelectedTab] = useState(
    isCPOwner || userComponentCode === COMPONENTS_CODE.Regular_Air_Force
      ? 0
      : userComponentCode === COMPONENTS_CODE.Air_Force_Reserve_Command
      ? 1
      : 2
  )

  const [mode, setMode] = useState<ModeType>(determineMode(previousPage, kpiClicked))

  const [inquiryFilter, setInquiryFilter] = useState({
    ...initialInquiryFilter,
    notSelfFilter: me.userId,
    ...UserRoleCodes,
  })
  const [searchValue, setSearchValue] = useState('')
  
  const defaultInquiryFilter = (selectedTab: number) => {
    if (selectedTab === 0) {
      setInquiryFilter({
        ...initialInquiryFilter,
        ...UserRoleCodes,
        assignedComponentCodeFilter: COMPONENTS_CODE.Regular_Air_Force,
        isArchivedFilter: false,
        notSelfFilter: me.userId,
        statusCodesOmittedFilter: [
          INQUIRY_STATUS_IDS.Saved,
          INQUIRY_STATUS_IDS.Resolved,
          INQUIRY_STATUS_IDS.Closed,
          INQUIRY_STATUS_IDS.Canceled,
        ],
      })
    } else if (selectedTab === 1) {
      setInquiryFilter({
        ...initialInquiryFilter,
        ...UserRoleCodes,
        assignedComponentCodeFilter: COMPONENTS_CODE.Air_Force_Reserve_Command,
        isArchivedFilter: false,
        notSelfFilter: me.userId,
        statusCodesOmittedFilter: [
          INQUIRY_STATUS_IDS.Saved,
          INQUIRY_STATUS_IDS.Resolved,
          INQUIRY_STATUS_IDS.Closed,
          INQUIRY_STATUS_IDS.Canceled,
        ],
      })
    } else if (selectedTab === 2) {
      setInquiryFilter({
        ...initialInquiryFilter,
        ...UserRoleCodes,
        assignedComponentCodeFilter: COMPONENTS_CODE.Air_National_Guard,
        isArchivedFilter: false,
        notSelfFilter: me.userId,
        statusCodesOmittedFilter: [
          INQUIRY_STATUS_IDS.Saved,
          INQUIRY_STATUS_IDS.Resolved,
          INQUIRY_STATUS_IDS.Closed,
          INQUIRY_STATUS_IDS.Canceled,
        ],
      })
    }
  }
  const defaultDueoutFilter = (selectedTab: number) => {
    if (selectedTab === 0) {
      setDueoutFilter({
        ...dueoutFilter,
        assignedComponentCodeFilter: 143,
      })
    } else if (selectedTab === 1) {
      setDueoutFilter({
        ...dueoutFilter,
        assignedComponentCodeFilter: 144,
      })
    } else if (selectedTab === 2) {
      setDueoutFilter({
        ...dueoutFilter,
        assignedComponentCodeFilter: 145,
      })
    }
  }
  //Used to determine which Inquiries to display to the user on page load.
  useEffect(() => {
    defaultInquiryFilter(selectedTab)
    defaultDueoutFilter(selectedTab)
  }, [])

  const [dueoutFilter, setDueoutFilter] = useState({
    ...initialDueOutFilter,
    ...UserRoleCodes,
  })
  const [tabVisibility, setTabVisibility] = useState<TabVisibility>(initialTabVisibility)

  const resetFilterOptions = (priorArgs, order, updatedArgs) => ({
    ...priorArgs,
    categoryCodeFilter: order && order >= 9 ? priorArgs.categoryCodeFilter : ALL_SELECT_VALUE,
    sectionCodeFilter: order && order >= 8 ? priorArgs.sectionCodeFilter : ALL_SELECT_VALUE,
    servicingByFilter: order && order >= 4 ? priorArgs.servicingByFilter : ALL_SELECT_VALUE_STRING,
    servicingMAJCOMCodeFilter:
      order && order >= 3 ? priorArgs.servicingMAJCOMCodeFilter : ALL_SELECT_VALUE,
    servicingOrganizationFilter:
      order && order >= 5 ? priorArgs.servicingOrganizationFilter : ALL_SELECT_VALUE_STRING,
    yearFilter: order && order >= 1 ? priorArgs.yearFilter : ALL_SELECT_VALUE,
    ...updatedArgs,
  })
  const updateInquiryFilter = (updatedArgs, order) => {
    setInquiryFilter((priorArgs) => resetFilterOptions(priorArgs, order, updatedArgs))
  }
  const updateDueoutFilter = (updatedArgs, order) => {
    setDueoutFilter((priorArgs) => resetFilterOptions(priorArgs, order, updatedArgs))
  }
  const { dueOutCount: dueoutCategoryCounts, dueOutCountIsLoading: dueoutCategoryCountIsLoading } =
    useDueoutorderByCounts({
      ...dueoutFilter,
      categories: categories, // Remove if not working
      groupBy: 'CategoryCode',
    })
  const { dueOutCount: dueOutStatusCount } = useDueoutorderByCounts({
    ...dueoutFilter,
    groupBy: 'StatusCode',
  })
  const { dueOutCount: dueOutPriorityCount } = useDueoutorderByCounts({
    ...dueoutFilter,
    groupBy: 'Priority',
  })
  const { dueOutCountValue: threeDaysAgoDueDateCount } = useDueOutCount({
    ...dueoutFilter,
    statusCodesOmittedFilter: [
      DUEOUT_STATUS_CODE.CANCELED,
      DUEOUT_STATUS_CODE.CLOSED,
    ],
    threeDaysAgoDueDateCount: true,
  })
  const { dueOutCountValue: sevevenDaysAgoDueDateCount } = useDueOutCount({
    ...dueoutFilter,
    sevevenDaysAgoDueDateCount: true,
    statusCodesOmittedFilter: [
      DUEOUT_STATUS_CODE.CANCELED,
      DUEOUT_STATUS_CODE.CLOSED,
    ],
  })
  const { dueOutCountValue: tenDaysAgoDueDateCount } = useDueOutCount({
    ...dueoutFilter,
    statusCodesOmittedFilter: [
      DUEOUT_STATUS_CODE.CANCELED,
      DUEOUT_STATUS_CODE.CLOSED,
    ],
    tenDaysAgoDueDateCount: true,
  })
  const { dueOutCountValue: morethanTenDaysAgoDueDateCount } = useDueOutCount({
    ...dueoutFilter,
    morethanTenDaysAgoDueDateCount: true,
    statusCodesOmittedFilter: [
      DUEOUT_STATUS_CODE.CANCELED,
      DUEOUT_STATUS_CODE.CLOSED,
    ],
  })
  const selectedTabId: number = useMemo(() => {
    setInquiryFilter((prev) => ({
      ...prev,
      categoryCodeFilter: ALL_SELECT_VALUE,
      sectionCodeFilter: ALL_SELECT_VALUE,
      servicingByFilter: ALL_SELECT_VALUE_STRING,
      servicingMAJCOMCodeFilter: ALL_SELECT_VALUE,
      servicingOrganizationFilter: ALL_SELECT_VALUE_STRING,
      subCategoryCodeFilter: ALL_SELECT_VALUE,
      yearFilter: ALL_SELECT_VALUE,
    }))
    return selectedTab === 1
      ? COMPONENTS_CODE.Air_Force_Reserve_Command
      : selectedTab === 2
      ? COMPONENTS_CODE.Air_National_Guard
      : COMPONENTS_CODE.Regular_Air_Force
  }, [selectedTab])

  const handleClearAll = () => {
    setSelectedTab(0)
    defaultInquiryFilter(selectedTab)

    setDueoutFilter({
      ...initialDueOutFilter,
      ...UserRoleCodes,
    })
  }

  //Dashboard Section Tab Counts
  const { inquiryCountValue: totalRegAFCount } = useInquiryCount({
    ...inquiryFilter,
    ...UserRoleCodes,
    assignedComponentCodeFilter: COMPONENTS_CODE.Regular_Air_Force,
    isArchivedFilter: false,
    notSelfFilter: me.userId,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })
  const { inquiryCountValue: totalAFRCCount } = useInquiryCount({
    ...inquiryFilter,
    ...UserRoleCodes,
    assignedComponentCodeFilter: COMPONENTS_CODE.Air_Force_Reserve_Command,
    isArchivedFilter: false,
    notSelfFilter: me.userId,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })
  const { inquiryCountValue: totalANGCount } = useInquiryCount({
    ...inquiryFilter,
    ...UserRoleCodes,
    assignedComponentCodeFilter: COMPONENTS_CODE.Air_National_Guard,
    isArchivedFilter: false,
    notSelfFilter: me.userId,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })

  const { inquiryCount: totalStatusCodeCount } = useInquiryCount({
    ...inquiryFilter,
    assignedComponentCodeFilter: selectedTabId,
    groupByArgs: ['StatusCode'],
    notSelfFilter: me.userId,
    selectArgs: ['InquiryGUID', 'StatusCode'],
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })
  const [statusCounts, setStatusCounts] = useState({
    cssInquiryCount: 0,
    leadershipInquiryCount: 0,
    reopenedInquiryCount: 0,
    sectionChiefInquiryCount: 0,
    submittedInquiryCount: 0,
    supervisorInquiryCount: 0,
    technicianInquiryCount: 0,
    totalActiveInquiryCount: 0,
    totalInquiryCount: 0,
    totalUnassignedInquiryCount: 0,
  })

  // Function to calculate statusCounts
  const calculateInquiryCounts = () => {
    const newCounts = {
      cssInquiryCount: 0,
      leadershipInquiryCount: 0,
      reopenedInquiryCount: 0,
      sectionChiefInquiryCount: 0,
      submittedInquiryCount: 0,
      supervisorInquiryCount: 0,
      technicianInquiryCount: 0,
      totalActiveInquiryCount: 0,
      totalInquiryCount: 0,
      totalUnassignedInquiryCount: 0,
    }

    totalStatusCodeCount.forEach((statusCode) => {
      switch (statusCode.StatusCode) {
        case INQUIRY_STATUS_IDS.Submitted:
          newCounts.submittedInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalUnassignedInquiryCount += statusCode.InquiryGUID_count
          break
        case INQUIRY_STATUS_IDS.Reopened:
          newCounts.reopenedInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalUnassignedInquiryCount += statusCode.InquiryGUID_count
          break
        case INQUIRY_STATUS_IDS.CustomerSupportCSS:
        case INQUIRY_STATUS_IDS.CareerDevelopmentCSS:
        case INQUIRY_STATUS_IDS.ForceManagementCSS:
        case INQUIRY_STATUS_IDS.InstallationPersonnelReadinessCSS:
        case INQUIRY_STATUS_IDS.PersonnelSystemsManagementCSS:
          newCounts.cssInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalActiveInquiryCount += statusCode.InquiryGUID_count
          break
        case INQUIRY_STATUS_IDS.CustomerSupportTechnician:
        case INQUIRY_STATUS_IDS.CareerDevelopmentTechnician:
        case INQUIRY_STATUS_IDS.ForceManagementTechnician:
        case INQUIRY_STATUS_IDS.InstallationPersonnelReadinessTechnician:
        case INQUIRY_STATUS_IDS.PersonnelSystemsManagementTechnician:
          newCounts.technicianInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalActiveInquiryCount += statusCode.InquiryGUID_count
          break
        case INQUIRY_STATUS_IDS.CustomerSupportSupervisor:
        case INQUIRY_STATUS_IDS.CareerDevelopmentSupervisor:
        case INQUIRY_STATUS_IDS.ForceManagementSupervisor:
        case INQUIRY_STATUS_IDS.InstallationPersonnelReadinessSupervisor:
        case INQUIRY_STATUS_IDS.PersonnelSystemsManagementSupervisor:
          newCounts.supervisorInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalActiveInquiryCount += statusCode.InquiryGUID_count
          break
        case INQUIRY_STATUS_IDS.CustomerSupportSectionChief:
        case INQUIRY_STATUS_IDS.CareerDevelopmentSectionChief:
        case INQUIRY_STATUS_IDS.ForceManagementSectionChief:
        case INQUIRY_STATUS_IDS.InstallationPersonnelReadinessSectionChief:
        case INQUIRY_STATUS_IDS.PersonnelSystemsManagementSectionChief:
          newCounts.sectionChiefInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalActiveInquiryCount += statusCode.InquiryGUID_count
          break
        case INQUIRY_STATUS_IDS.MpfLeadership:
          newCounts.leadershipInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalInquiryCount += statusCode.InquiryGUID_count
          newCounts.totalActiveInquiryCount += statusCode.InquiryGUID_count
          break
        default:
          break
      }
    })

    setStatusCounts(newCounts)
  }

  // Effect to recalculate counts when totalStatusCodeCount changes
  useEffect(() => {
    calculateInquiryCounts()
  }, [totalStatusCodeCount])

  // By Source Counts
  const { inquiryCount: totalSourceCounts } = useInquiryCount({
    ...inquiryFilter,
    groupByArgs: ['CurrentlyServicingBy'],
    selectArgs: ['InquiryGUID', 'CurrentlyServicingBy'],
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })

  const inquirySourceCustomerCount: number =
    totalSourceCounts.find((countObj) => countObj.CurrentlyServicingBy === 'MPF')
      ?.InquiryGUID_count || 0

  const inquirySourceCSSCount: number =
    totalSourceCounts.find((countObj) => countObj.CurrentlyServicingBy === 'CSS')
      ?.InquiryGUID_count || 0

  const { dueOutCountValue: kpiFilteredDueOutCountRegAF } = useDueOutCount({
    ...dueoutFilter,
    assignedComponentCodeFilter: 143,
  })
  const { dueOutCountValue: kpiFilteredDueOutCountAFRC } = useDueOutCount({
    ...dueoutFilter,
    assignedComponentCodeFilter: 144,
  })
  const { dueOutCountValue: kpiFilteredDueOutCountANG } = useDueOutCount({
    ...dueoutFilter,
    assignedComponentCodeFilter: 145,
  })
  const determineDashboardCount = (tab: number, kpiTitle: string) => {
    if (kpiTitle === 'DUE OUTS') {
      if (tab === 0) {
        return twoDigits(kpiFilteredDueOutCountRegAF)
      } else if (tab === 1) {
        return twoDigits(kpiFilteredDueOutCountAFRC)
      } else if (tab === 2) {
        return twoDigits(kpiFilteredDueOutCountANG)
      }
    }
    if (kpiTitle === 'TOTAL INQUIRIES') {
      if (tab === 0) {
        return twoDigits(totalRegAFCount)
      } else if (tab === 1) {
        return twoDigits(totalAFRCCount)
      } else if (tab === 2) {
        return twoDigits(totalANGCount)
      }
    }
  }
  const dashboardKPIs = [
    {
      count: determineDashboardCount(selectedTab, 'TOTAL INQUIRIES'),
      kpicode: '1',
      title: 'TOTAL INQUIRIES',
    },
    {
      count: twoDigits(statusCounts.totalUnassignedInquiryCount),
      kpicode: '1',
      title: 'UNASSIGNED',
    },
    {
      count: twoDigits(statusCounts.totalActiveInquiryCount),
      kpicode: '1',
      title: 'ACTIVE',
    },
    {
      border: 'border-left1 dashed-border',
      count: determineDashboardCount(selectedTab, 'DUE OUTS'),
      kpicode: '1',
      name: 'dueout',
      title: 'DUE OUTS',
    },
  ]
  const { inquiryCountValue: inquiryPendingThreeDays } = useInquiryCount({
    ...inquiryFilter,
    pendingActionOutsideThreeDaysFilter: true,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })

  const { inquiryCountValue: inquiryPendingSevenDays } = useInquiryCount({
    ...inquiryFilter,
    pendingActionOutsideSevenDaysFilter: true,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })

  const { inquiryCountValue: inquiryPendingTenDays } = useInquiryCount({
    ...inquiryFilter,
    pendingActionOutsideTenDaysFilter: true,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })

  const { inquiryCountValue: inquiryPendingMoreThanTenDays } = useInquiryCount({
    ...inquiryFilter,
    pendingActionOutsideMoreThanTenFilter: true,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })

  const { inquiryCountValue: inquiryCustomerThreeDays } = useInquiryCount({
    ...inquiryFilter,
    customerActionRequiredThreeDaysFilter: true,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })

  const { inquiryCountValue: inquiryCustomerSevenDays } = useInquiryCount({
    ...inquiryFilter,
    customerActionRequiredSevenDaysFilter: true,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })

  const { inquiryCountValue: inquiryCustomerTenDays } = useInquiryCount({
    ...inquiryFilter,
    customerActionRequiredTenDaysFilter: true,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })

  const { inquiryCountValue: inquiryCustomerMoreThanTenDays } = useInquiryCount({
    ...inquiryFilter,
    customerActionRequiredMoreThanTenDaysFilter: true,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })

  const { inquiryCount: totalByMonthCounts, inquiryCountIsLoading: totalsByMonthIsLoading } =
    useTotalByMonthCounts({
      ...inquiryFilter,
      statusCodesOmittedFilter: [INQUIRY_STATUS_IDS.Saved, INQUIRY_STATUS_IDS.Canceled],
    })

  // Category Counts
  const {
    inquiryCount: inquiryCategoryCounts,
    inquiryCountIsLoading: inquiryCategoryCountIsLoading,
  } = useInquiryCategoryCounts({
    ...inquiryFilter,
    // ...UserRoleCodes,
    categories: categories,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
  })
  const { inquiryDistinctValues, inquiryDistinctValuesAreLoading } = useInquiryDistinctValues({
    ...UserRoleCodes,
    assignedComponentCodeFilter: selectedTabId,
    categoryCodeFilter: inquiryFilter.categoryCodeFilter as number,
    distinctFiltersToQuery: [
      INQUIRY_COLUMN_NAMES.FY,
      INQUIRY_COLUMN_NAMES.AssignedComponentCode,
      INQUIRY_COLUMN_NAMES.ServicingMAJCOMCode,
      INQUIRY_COLUMN_NAMES.ServicingBy,
      INQUIRY_COLUMN_NAMES.ServicingOrganization,
      INQUIRY_COLUMN_NAMES.SectionCode,
      INQUIRY_COLUMN_NAMES.CategoryCode,
    ],
    isArchivedFilter: false,
    notSelfFilter: me.userId,
    savedFilter: false,
    sectionCodeFilter: inquiryFilter.sectionCodeFilter,
    servicingByFilter: inquiryFilter.servicingByFilter,
    servicingMAJCOMCodeFilter: inquiryFilter.servicingMAJCOMCodeFilter,
    servicingOrganizationFilter: inquiryFilter.servicingOrganizationFilter,
    statusCodesOmittedFilter: [
      INQUIRY_STATUS_IDS.Saved,
      INQUIRY_STATUS_IDS.Resolved,
      INQUIRY_STATUS_IDS.Closed,
      INQUIRY_STATUS_IDS.Canceled,
    ],
    subCategoryCodeFilter: inquiryFilter.subCategoryFilter as number,
    yearFilter: inquiryFilter.yearFilter,
  })
  const { dueOutDistinctValues, dueOutDistinctValuesAreLoading } = useDueOutDistinctValues({
    ...UserRoleCodes,
    assignedComponentCodeFilter: selectedTabId,
    categoryCodeFilter: dueoutFilter.categoryCodeFilter as number,
    distinctFiltersToQuery: [
      DUEOUT_COLUMN_NAMES.FY,
      DUEOUT_COLUMN_NAMES.AssignedComponentCode,
      DUEOUT_COLUMN_NAMES.ServicingMAJCOMCode,
      DUEOUT_COLUMN_NAMES.ServicingOrganization,
      DUEOUT_COLUMN_NAMES.ServicingBy,
      DUEOUT_COLUMN_NAMES.SectionCode,
      DUEOUT_COLUMN_NAMES.CategoryCode,
    ],
    hasClosedAndCanceled: false,
    isArchivedFilter: false,
    savedFilter: false,
    sectionCodeFilter: dueoutFilter.sectionCodeFilter,
    servicingByFilter: dueoutFilter.servicingByFilter,
    servicingMAJCOMCodeFilter: dueoutFilter.servicingMAJCOMCodeFilter,
    servicingOrganizationFilter: dueoutFilter.servicingOrganizationFilter,
    subCategoryCodeFilter: dueoutFilter.subCategoryCodeFilter as number,
    yearFilter: dueoutFilter.yearFilter,
  })

  const [initialComponentDropdown, setInitialComponentDropdown] = useState<optionsTypes[]>([
    ALL_SELECT_OPTION,
  ])
  const [initialMajcomDropdown, setInitialMajcomDropdown] = useState<optionsTypes[]>([
    ALL_SELECT_OPTION,
  ])
  const [initialSectionDropdown, setInitialSectionDropdown] = useState<optionsTypes[]>([
    ALL_SELECT_OPTION,
  ])
  const [initialCategoryDropdown, setInitialCategoryDropdown] = useState<optionsTypes[]>([
    ALL_SELECT_OPTION,
  ])
  const [initialDropDownValues, setInitialDropDownValues] = useState<InitialContentDropDownValues>({
    categoryValue: ALL_SELECT_OPTION,
    componentValue: ALL_SELECT_OPTION,
    majcomValue: ALL_SELECT_OPTION,
    sectionValue: ALL_SELECT_OPTION,
  })


  const [majcomOptions, setMajcomOptions] = useState<optionsTypes[]>([])
  const [categoryOptions, setCategoryOptions] = useState<optionsTypes[]>([])

  const adminTabs = [
    {
      content: (
        <AdminDashBoardTabContent
          categories={categories}
          dashboardKPIs={dashboardKPIs}
          dueOutCount={kpiFilteredDueOutCountRegAF}
          dueOutPriorityCount={dueOutPriorityCount}
          dueOutStatusCount={dueOutStatusCount}
          dueoutCategoryCounts={dueoutCategoryCounts}
          inquiryCategoryCounts={inquiryCategoryCounts}
          inquiryCustomer={{
            inquiryCustomerMoreThanTenDays,
            inquiryCustomerSevenDays,
            inquiryCustomerTenDays,
            inquiryCustomerThreeDays,
          }}
          inquiryFilter={inquiryFilter}
          inquiryPending={{
            inquiryPendingMoreThanTenDays,
            inquiryPendingSevenDays,
            inquiryPendingTenDays,
            inquiryPendingThreeDays,
          }}
          inquirySourceCSSCount={inquirySourceCSSCount}
          inquirySourceCustomerCount={inquirySourceCustomerCount}
          mode={mode}
          morethanTenDaysAgoDueDateCount={
            morethanTenDaysAgoDueDateCount
          }
          setInquiryFilter={setInquiryFilter}
          sevevenDaysAgoDueDateCount={sevevenDaysAgoDueDateCount}
          statusCounts={statusCounts}
          tab={143}
          tenDaysAgoDueDateCount={tenDaysAgoDueDateCount}
          threeDaysAgoDueDateCount={threeDaysAgoDueDateCount}
          totalByMonthCounts={totalByMonthCounts}
        />
      ),
      count:
        mode === 'inquiry' ? twoDigits(totalRegAFCount) : twoDigits(kpiFilteredDueOutCountRegAF),
      id: 143,
      isActive: selectedTab === 0,
      isVisible: tabVisibility.regAF,
      tabTitle: 'Regular Air Force',
    },
    {
      content: (
        <AdminDashBoardTabContent
          categories={categories}
          dashboardKPIs={dashboardKPIs}
          dueOutCount={kpiFilteredDueOutCountAFRC}
          dueOutPriorityCount={dueOutPriorityCount}
          dueOutStatusCount={dueOutStatusCount}
          dueoutCategoryCounts={dueoutCategoryCounts}
          inquiryCategoryCounts={inquiryCategoryCounts}
          inquiryCustomer={{
            inquiryCustomerMoreThanTenDays,
            inquiryCustomerSevenDays,
            inquiryCustomerTenDays,
            inquiryCustomerThreeDays,
          }}
          inquiryFilter={inquiryFilter}
          inquiryPending={{
            inquiryPendingMoreThanTenDays,
            inquiryPendingSevenDays,
            inquiryPendingTenDays,
            inquiryPendingThreeDays,
          }}
          inquirySourceCSSCount={inquirySourceCSSCount}
          inquirySourceCustomerCount={inquirySourceCustomerCount}
          mode={mode}
          morethanTenDaysAgoDueDateCount={morethanTenDaysAgoDueDateCount}
          setInquiryFilter={setInquiryFilter}
          sevevenDaysAgoDueDateCount={sevevenDaysAgoDueDateCount}
          statusCounts={statusCounts}
          tab={144}
          tenDaysAgoDueDateCount={tenDaysAgoDueDateCount}
          threeDaysAgoDueDateCount={threeDaysAgoDueDateCount}
          totalByMonthCounts={totalByMonthCounts}
        />
      ),
      count: mode === 'inquiry' ? twoDigits(totalAFRCCount) : twoDigits(kpiFilteredDueOutCountAFRC),
      id: 144,
      isActive: selectedTab === 1,
      isVisible: tabVisibility.afrc,
      tabTitle: 'Air Force Reserve Command',
    },

    {
      content: (
        <AdminDashBoardTabContent
          categories={categories}
          dashboardKPIs={dashboardKPIs}
          dueOutCount={kpiFilteredDueOutCountANG}
          dueOutPriorityCount={dueOutPriorityCount}
          dueOutStatusCount={dueOutStatusCount}
          dueoutCategoryCounts={dueoutCategoryCounts}
          inquiryCategoryCounts={inquiryCategoryCounts}
          inquiryCustomer={{
            inquiryCustomerMoreThanTenDays,
            inquiryCustomerSevenDays,
            inquiryCustomerTenDays,
            inquiryCustomerThreeDays,
          }}
          inquiryFilter={inquiryFilter}
          inquiryPending={{
            inquiryPendingMoreThanTenDays,
            inquiryPendingSevenDays,
            inquiryPendingTenDays,
            inquiryPendingThreeDays,
          }}
          inquirySourceCSSCount={inquirySourceCSSCount}
          inquirySourceCustomerCount={inquirySourceCustomerCount}
          mode={mode}
          morethanTenDaysAgoDueDateCount={morethanTenDaysAgoDueDateCount}
          setInquiryFilter={setInquiryFilter}
          sevevenDaysAgoDueDateCount={sevevenDaysAgoDueDateCount}
          statusCounts={statusCounts}
          tab={145}
          tenDaysAgoDueDateCount={tenDaysAgoDueDateCount}
          threeDaysAgoDueDateCount={threeDaysAgoDueDateCount}
          totalByMonthCounts={totalByMonthCounts}
        />
      ),
      count: mode === 'inquiry' ? twoDigits(totalANGCount) : twoDigits(kpiFilteredDueOutCountANG),
      id: 145,
      isActive: selectedTab === 2,
      isVisible: tabVisibility.ang,
      tabTitle: 'Air National Guard',
    },
  ]
  const onChangeComponent = (e: { label: string; value: number }) => {
    const option = majcoms
      .filter(({ ParentComponentCode }) => ParentComponentCode === e.value)
      .map(({ Label, Value }) => ({ label: Label, value: Value }))
    const initialMAJCOMCodes = initialMajcomDropdown.map(({ value }) => value)
    const options = option.filter(({ value }) => initialMAJCOMCodes.includes(value as number))
    if (options.length > 1) options.unshift(ALL_SELECT_OPTION)
    setComponentValue({ label: e.label, value: e.value })
    setMajcomValue(options.length > 1 ? ALL_SELECT_OPTION : options[0])
    setMajcomOptions(options)
  }
  const onChangeMajcom = (e: { label: string; value: number }) => {
    setMajcomValue({ label: e.label, value: e.value })
  }
  const onChangeSection = (e: { label: string; value: number }) => {
    const options: optionsTypes[] =
      e.value === ALL_SELECT_VALUE
        ? initialCategoryDropdown
        : categories
            .filter(({ ParentSectionCode }) => ParentSectionCode === e.value)
            .map(({ Label, Value }) => ({ label: Label, value: Value }))

    if (options.length > 0 && e.value !== ALL_SELECT_VALUE) {
      options.unshift(ALL_SELECT_OPTION)
    }
    setSectionValue({ label: e.label, value: e.value })
    setCategoryValue(ALL_SELECT_OPTION)
    setCategoryOptions(options)
  }
  const onChangeCategory = (e: { label: string; value: number }) => {
    setCategoryValue({ label: e.label, value: e.value })
  }
  const onClear = () => {
    setMajcomOptions(initialMajcomDropdown)
    setCategoryOptions(initialCategoryDropdown)
    setComponentValue(initialDropDownValues.componentValue)
    setMajcomValue(initialDropDownValues.majcomValue)
    setSectionValue(initialDropDownValues.sectionValue)
    setCategoryValue(initialDropDownValues.categoryValue)
    setSearchValue('')
  }
  useEffect(() => {
    setTabVisibility({
      afrc:
        isSiteAdmin ||
        isCPOwner ||
        isAFRCOwner ||
        servincingComponent === 'Air Force Reserve Command',
      ang: isSiteAdmin || isCPOwner || isANGOwner || servincingComponent === 'Air National Guard',
      regAF:
        isSiteAdmin || isCPOwner || isRegAFOwner || servincingComponent === 'Regular Air Force',
    })
  }, [isSiteAdmin, isCPOwner, isAssignedComponentOwner, servincingComponent])
  const { pascodeMetadata } = usePascodeMetadata({
    IsArchived: false,
  })
  const setInitialOptions = (
    componentOptions: optionsTypes[],
    majcomOptions: optionsTypes[],
    sectionOptions: optionsTypes[],
    categoryOptions: optionsTypes[],
    ownerInitialMajcom?: optionsTypes[]
  ) => {
    const componentValue = userProfile[0].AssignedComponentCode
    const userComponenetValue = componentOptions.filter(({ value }) => value === componentValue)[0]
    const userMajcomValue = majcomOptions.filter(
      ({ label }) => label === userProfile[0].ServicingMAJCOM
    )[0]
    setInitialComponentDropdown(componentOptions)
    setInitialMajcomDropdown(majcomOptions)
    setMajcomOptions(ownerInitialMajcom ? ownerInitialMajcom : majcomOptions)
    setInitialSectionDropdown(sectionOptions)
    setInitialCategoryDropdown(categoryOptions)
    setCategoryOptions(categoryOptions)
    const dropdownValues: InitialContentDropDownValues = {
      categoryValue: categoryOptions.length === 1 ? categoryOptions[0] : ALL_SELECT_OPTION,
      componentValue: userComponenetValue,
      majcomValue: userMajcomValue,
      sectionValue: sectionOptions.length === 1 ? sectionOptions[0] : ALL_SELECT_OPTION,
    }
    setInitialDropDownValues({ ...dropdownValues })
    setComponentValue(dropdownValues.componentValue)
    setMajcomValue(dropdownValues.majcomValue)
    setSectionValue(dropdownValues.sectionValue)
    setCategoryValue(dropdownValues.categoryValue)
  }

  useEffect(() => {
    const assignedComponentOptions = ComponentOptions.filter(
      ({ value }) => value !== ALL_SELECT_VALUE
    )
    if (isCPOwner || isSiteAdmin) {
      const profileComponentCode = userProfile[0].AssignedComponentCode
      const majcomCodes = majcoms
        .filter((item) => item.ParentComponentCode === profileComponentCode)
        .map((item) => item.MAJCOMCode)
      const majcomOption = generateCodeOptionOnLength(MajcomOptions, majcomCodes)
      setInitialOptions(
        assignedComponentOptions,
        MajcomOptions,
        SectionOptions,
        CategoryOptions,
        majcomOption
      )
    } else if (isAssignedComponentOwner) {
      const componentCodes = UserRoleCodes.userLoginRole?.usersAssignedComponentCode
      const assignedComponentCodeOptions = generateCodeOptionOnLength(
        assignedComponentOptions,
        componentCodes
      )
      const majcomCodes = majcoms
        .filter((item) => componentCodes?.includes(item.ParentComponentCode))
        .map((item) => item.MAJCOMCode)
      const majcomOptions = generateCodeOptionOnLength(MajcomOptions, majcomCodes)
      setInitialOptions(
        assignedComponentCodeOptions,
        majcomOptions,
        SectionOptions,
        CategoryOptions
      )
    } else if (isMAJCOMOwner) {
      const majcomCodes = UserRoleCodes.userLoginRole?.usersMajcomCodeFilter
      const componentCodes = majcoms
        .filter((item) => majcomCodes?.includes(item.MAJCOMCode))
        .map((item) => item.ParentComponentCode)
      const assignedComponentCodeOptions = generateCodeOptionOnLength(
        assignedComponentOptions,
        componentCodes
      )
      const majcomOptions = generateCodeOptionOnLength(MajcomOptions, majcomCodes)
      setInitialOptions(
        assignedComponentCodeOptions,
        majcomOptions,
        SectionOptions,
        CategoryOptions
      )
    } else if (isLeadership || isMPFOwner) {
      const organizations = UserRoleCodes.userLoginRole
        ?.usersServicingOrganizationWithoutSectionFilter as string[]
      const filteredItems: PascodeMetadata[] = getFilteredItems(pascodeMetadata, organizations)
      const { componentCodes, servicingMAJCOMS } = getUniqueCodes(filteredItems)
      const majcomCodes = servicingMAJCOMS.map((item) => getMAJCOMCode(item, majcoms))
      const assignedComponentCodeOptions = generateCodeOptionOnLength(
        assignedComponentOptions,
        componentCodes
      )
      const majcomOptions = generateCodeOptionOnLength(MajcomOptions, majcomCodes)
      setInitialOptions(
        assignedComponentCodeOptions,
        majcomOptions,
        SectionOptions,
        CategoryOptions
      )
    } else if (isCSS) {
      const cssPASCODES = UserRoleCodes.userLoginRole?.CSSPASCODEFilter
      const filteredItems = pascodeMetadata.filter((item) => cssPASCODES?.includes(item.PASCODE))
      const { componentCodes, servicingMAJCOMS } = getUniqueCodes(filteredItems)
      const majcomCodes = servicingMAJCOMS.map((item) => getMAJCOMCode(item, majcoms))
      const assignedComponentCodeOptions = generateCodeOptionOnLength(
        assignedComponentOptions,
        componentCodes
      )
      const majcomOptions = generateCodeOptionOnLength(MajcomOptions, majcomCodes)
      setInitialOptions(
        assignedComponentCodeOptions,
        majcomOptions,
        SectionOptions,
        CategoryOptions
      )
    } else {
      const organizations = UserRoleCodes.userLoginRole
        ?.usersServicingOrganizationFilter as string[]
      const filteredItems = getFilteredItems(pascodeMetadata, organizations)
      const { componentCodes, servicingMAJCOMS } = getUniqueCodes(filteredItems)
      const majcomCodes = servicingMAJCOMS.map((item) => getMAJCOMCode(item, majcoms))
      const assignedComponentCodeOptions = generateCodeOptionOnLength(
        assignedComponentOptions,
        componentCodes
      )
      const majcomOptions = generateCodeOptionOnLength(MajcomOptions, majcomCodes)
      const sectionCodes = UserRoleCodes.userLoginRole?.usersSectionCodeFilter as number[]
      const CategoryCodes = categories
        .filter((item) => sectionCodes?.includes(item.ParentSectionCode as number))
        .map((item) => item.CategoryCode)
      const sectionOptions = generateCodeOptionOnLength(SectionOptions, sectionCodes)
      const categoryOptions = generateCodeOptionOnLength(CategoryOptions, CategoryCodes)
      setInitialOptions(
        assignedComponentCodeOptions,
        majcomOptions,
        sectionOptions,
        categoryOptions
      )
    }
  }, [isSiteAdmin, isMAJCOMOwner, isCPOwner, isAssignedComponentOwner, pascodeMetadata])
  return (
    <div className='adminview'>
      {(inquiryCategoryCountIsLoading ||
        dueOutDistinctValuesAreLoading ||
        inquiryDistinctValuesAreLoading) && <PageOverlayBusy />}
      <div className='py-2 px-3 whitebg shadow1 d-flex flex-column flex-sm-row align-items-md-center flex-wrap'>
        <div className='d-flex align-items-center py-1'>
          <span className='icon-dashboard2 me-1 color-primary'></span>{' '}
          <h2 aria-label='Dashboard' className='poppins-semibold font-16 title-color4' tabIndex={0}>
            Dashboard
          </h2>
        </div>
        <div className='d-flex flex-wrap flex-column flex-sm-row justify-content-sm-end gap-1 ms-sm-auto'>
          <AdminDashboardFilters
            availableFilterValues={
              mode === 'inquiry' ? inquiryDistinctValues[0] : dueOutDistinctValues[0]
            }
            dueOutFilter={dueoutFilter}
            filter={mode === 'inquiry' ? inquiryFilter : dueoutFilter}
            inquiryFilter={inquiryFilter}
            mode={mode}
            onClearAll={handleClearAll}
            onUpdateFilter={mode === 'inquiry' ? updateInquiryFilter : updateDueoutFilter}
            selectedTab={selectedTab}
            setMode={setMode}
          />
        </div>
      </div>
      <div className='container'>
        <div className='row'>
          <div className='col-sm-12 mt-0 py-1 admintabs'>
            <Tabs
              anchorclass='font-12 poppins-semibold'
              badge='badge border-radius3 px-2 py-1 font-12 poppins-semibold'
              className='xl'
              filter={mode === 'dueout' ? dueoutFilter : inquiryFilter}
              items={adminTabs}
              onTabSelected={setSelectedTab}
              setFilter={mode === 'dueout' ? setDueoutFilter : setInquiryFilter}
              tablistclassName='overflow-auto'
            />
          </div>
        </div>
      </div>

      <div className='container'>
        <div className='row'>
          <div className='col-sm-12 my-2'>
            <div className='shadow card w-100 px-3 py-3'>
              <div className='d-flex'>
                <div className='ms-auto d-flex flex-wrap align-items-center gap-1'>
                  <div className='mx-1'>
                    <CustomSelect
                      className='form-sm w-100 max-w-sm-150 min-w-sm-150  sourcesanspro font-13 customselect'
                      formClassName='form-horizontal dash poppins-semibold font-11'
                      id='AssignComponent'
                      inputProps={{
                        className: 'mb-1 font-12 poppins-medium title-color',
                      }}
                      label='Assigned Component'
                      name='Assigned Component'
                      onChange={onChangeComponent}
                      options={initialComponentDropdown}
                      value={
                        initialComponentDropdown?.length === 1
                          ? initialComponentDropdown[0]?.value
                          : componentValue?.value ?? ALL_SELECT_VALUE
                      }
                    />
                  </div>
                  <div className='mx-1'>
                    <CustomSelect
                      className='form-sm w-100 max-w-sm-100 min-w-sm-100  sourcesanspro font-13 customselect'
                      formClassName='form-horizontal  dash poppins-semibold font-11'
                      id='ServicingMAJCOM'
                      inputProps={{
                        className: 'mb-1 font-12 poppins-medium title-color',
                      }}
                      label='Servicing MAJCOM'
                      name='Servicing MAJCOM'
                      onChange={onChangeMajcom}
                      options={majcomOptions}
                      value={
                        majcomOptions?.length === 1
                          ? majcomOptions[0]?.value
                          : majcomValue?.value ?? ALL_SELECT_VALUE
                      }
                    />
                  </div>
                  <div className='mx-1'>
                    <CustomSelect
                      className='form-sm w-100 max-w-sm-80 min-w-sm-80  sourcesanspro font-13 customselect'
                      formClassName='form-horizontal dash poppins-semibold font-11'
                      id='Section'
                      label='Section'
                      name='Section'
                      onChange={onChangeSection}
                      options={initialSectionDropdown}
                      value={
                        initialSectionDropdown?.length === 1
                          ? initialSectionDropdown[0]?.value
                          : sectionValue?.value ?? ALL_SELECT_VALUE
                      }
                    />
                  </div>
                  <div className='mx-1'>
                    <CustomSelect
                      className='form-sm w-100 max-w-sm-80 min-w-sm-80  sourcesanspro font-13 customselect'
                      formClassName='form-horizontal dash poppins-semibold font-11'
                      id='Category'
                      label='Category'
                      name='Category'
                      onChange={onChangeCategory}
                      options={categoryOptions}
                      value={
                        categoryOptions?.length === 1
                          ? categoryOptions[0]?.value
                          : categoryValue?.value ?? ALL_SELECT_VALUE
                      }
                    />
                  </div>

                  <Buttons
                    aria-label='Clear All'
                    className='btn btn-sm links ms-auto ms-sm-0 ms-md-0 text-nowrap links-underline'
                    label='Clear All'
                    onClick={onClear}
                  />
                </div>
              </div>
              <div className='row'>
                <div className='col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch'>
                  <div className='card w-100 bordered13 border-radius1'>
                    <HomeCalendar
                      CategoryOptions={CategoryOptions}
                      SectionOptions={SectionOptions}
                      filterCategoryValue={categoryValue}
                      filterComponentValue={componentValue}
                      filterMajcomValue={majcomValue}
                      filterSectionValue={sectionValue}
                    />
                  </div>
                </div>
                <div className='col-sm-12 col-md-6  col-xl-4 d-flex align-items-stretch'>
                  <div className='card w-100 bordered13 border-radius1'>
                    <Announcements
                      CategoryOptions={CategoryOptions}
                      SectionOptions={SectionOptions}
                      filterCategoryValue={categoryValue}
                      filterComponentValue={componentValue}
                      filterMajcomValue={majcomValue}
                      filterSectionValue={sectionValue}
                    />
                  </div>
                </div>

                <div className='col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch'>
                  <div className='card w-100 kb bordered13 border-radius1'>
                    <KnowledgeBase
                      CategoryOptions={CategoryOptions}
                      SectionOptions={SectionOptions}
                      filterCategoryValue={categoryValue}
                      filterComponentValue={componentValue}
                      filterMajcomValue={majcomValue}
                      filterSectionValue={sectionValue}
                      searchValue={searchValue}
                      setSearchValue={setSearchValue}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default AdminView
export { ModeType }
